import { useCallback, useEffect, useRef } from 'react';
import type { GridImperativeAPI } from 'react-window';
import { useGridRef, useListRef, type ListImperativeAPI } from 'react-window';

interface UseListScrollTrackingParams {
  selectedPage?: number;
  onPageChange?: (page: number) => void;
  totalPages: number;
}

interface UseGridScrollTrackingParams extends UseListScrollTrackingParams {
  columnCount: number;
}

interface UsePageScrollTrackingResult<TContainer> {
  containerRef: React.MutableRefObject<TContainer | null>;
  isProgrammaticScrollRef: React.MutableRefObject<boolean>;
  currentPageRef: React.MutableRefObject<number>;
  scrollToPage: (page: number) => void;
}

export const useListScrollTracking = ({
  selectedPage,
  onPageChange,
  totalPages,
}: UseListScrollTrackingParams): UsePageScrollTrackingResult<ListImperativeAPI> => {
  const isProgrammaticScrollRef = useRef(false);

  const containerRef = useListRef(null);
  const currentPageRef = useRef<number>(1);

  // scroll to a page programmatically
  const scrollToPage = useCallback(
    (page: number) => {
      if (!containerRef.current) return;
      isProgrammaticScrollRef.current = true;
      const safeIndex = Math.max(0, Math.min(page - 1, totalPages - 1));

      containerRef.current.scrollToRow({
        index: safeIndex,
        behavior: 'instant',
        align: 'auto',
      });
      currentPageRef.current = safeIndex + 1;
      onPageChange?.(currentPageRef.current);

      // Release guard after layout settles
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          isProgrammaticScrollRef.current = false;
        });
      });
    },
    [containerRef, onPageChange, totalPages]
  );

  // react to external selectedPage changes
  useEffect(() => {
    const page = selectedPage;
    if (page && page !== currentPageRef.current) {
      scrollToPage(page);
    }
  }, [scrollToPage, selectedPage]);

  return {
    containerRef,
    isProgrammaticScrollRef,
    currentPageRef,
    scrollToPage,
  };
};
export const useGridScrollTracking = ({
  selectedPage,
  onPageChange,
  columnCount,
  totalPages,
}: UseGridScrollTrackingParams): UsePageScrollTrackingResult<GridImperativeAPI> => {
  const isProgrammaticScrollRef = useRef(false);

  const containerRef = useGridRef(null);
  const currentPageRef = useRef<number>(1);

  // scroll to a page programmatically
  const scrollToPage = useCallback(
    (page: number) => {
      if (!containerRef.current) return;
      isProgrammaticScrollRef.current = true;

      const cellIndex = page - 1;
      if (cellIndex < 0 || cellIndex >= totalPages) return;
      const rowIndex = Math.floor(cellIndex / columnCount);
      const columnIndex = cellIndex % columnCount;

      containerRef.current.scrollToCell({
        rowIndex,
        columnIndex,
        behavior: 'instant',
        rowAlign: 'center',
      });
      currentPageRef.current = cellIndex + 1;
      onPageChange?.(currentPageRef.current);

      // Release guard after layout settles
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          isProgrammaticScrollRef.current = false;
        });
      });
    },
    [columnCount, containerRef, onPageChange, totalPages]
  );

  // react to external selectedPage changes
  useEffect(() => {
    const page = selectedPage;
    if (page && page !== currentPageRef.current) {
      scrollToPage(page);
    }
  }, [scrollToPage, selectedPage]);

  return {
    containerRef,
    isProgrammaticScrollRef,
    currentPageRef,
    scrollToPage,
  };
};
